import { vec3 } from 'gl-matrix';
import { atom, selector } from 'recoil';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import type { Image3D } from '~/domain/patient/image3d';
import { recoilPersist } from 'recoil-persist';
import {
    AdjacentSliceGhostDepths,
    DEFAULT_ADJACENT_SLICE_GHOST_DEPTHS,
} from '~/engine/core/render/renderers/slices/adjacentSliceGhost';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';

const { persistAtom } = recoilPersist({
    key: 'viewerPersist',
    storage: localStorage,
    converter: JSON,
});

// REMOVE IN FUTURE: This is just a flag for export modal rendering purposes...
export const exportableItemsCountState = atom<number>({
    key: 'exportableItemsCountState',
    default: 0,
});

/** What a properties panel shows: the dose or the selected structure. */
export type SidePropertiesSubject = 'dose' | 'structure';

// The properties panel open beside the sidebar, or above the row footer for
// the dose. At most one is open: opening another
// replaces it.
export const openSidePropertiesState = atom<SidePropertiesSubject | null>({
    key: 'openSidePropertiesState',
    default: null,
});

// Session-scoped: whether the 3D structure preview is switched on. It behaves
// as a tool rather than a per-ROI window: while on it shows whatever is
// selected, one ROI or a bulk selection, and follows the selection as it
// changes. It is opt-in per explicit click, so selecting an ROI never switches
// it on, and is not persisted so it never lingers across sessions.
export const roiPreview3DOpenState = atom<boolean>({
    key: 'roiPreview3DOpenState',
    default: false,
});

// Session-scoped, never persisted: whether the selected structure's contours
// from the slices next to the displayed one are drawn on it as "ghost" lines.
export const adjacentSliceGhostsEnabledState = atom<boolean>({
    key: 'adjacentSliceGhostsEnabledState',
    default: false,
});

// Session-scoped, never persisted: which slices the ghosts show on each side,
// kept apart from the switch so turning the aid off and on keeps the choice.
export const adjacentSliceGhostDepthsState = atom<AdjacentSliceGhostDepths>({
    key: 'adjacentSliceGhostDepthsState',
    default: DEFAULT_ADJACENT_SLICE_GHOST_DEPTHS,
});

// Session-scoped: whether the anterior MIP overview of the primary series is
// shown. Opt-in per explicit click, like the 3D preview above, and likewise
// not persisted.
export const seriesMipPreviewVisibleState = atom<boolean>({
    key: 'seriesMipPreviewVisibleState',
    default: false,
});

// Session-scoped, not persisted: the series of the row the PET projection is
// open for, or null while it is closed.
export const petMipRowSeriesState = atom<SeriesInstanceUid | null>({
    key: 'petMipRowSeriesState',
    default: null,
});

export interface PetMipViewSettings {
    /** About the patient's superior axis, degrees; 0 is anterior. */
    readonly azimuthDeg: number;
    /** Dark for high uptake rather than light. */
    readonly isInverted: boolean;
}

// Session-scoped; shared by the PET widget body and its title-bar controls.
export const petMipViewSettingsState = atom<PetMipViewSettings>({
    key: 'petMipViewSettingsState',
    default: { azimuthDeg: 0, isInverted: true },
});

export const busyIndicatorState = atom<{
    isOpen: boolean;
    message?: string;
}>({
    key: 'busyIndicatorState',
    default: {
        isOpen: false,
    },
});

export const showSensitiveInfoState = atom<boolean>({
    // This atom is used to control the visibility of sensitive patient information e.g. patient name, patient ID, etc.
    key: 'showSensitiveInfoState',
    default: true,
});

// Drives the shared PHI-reveal confirmation dialog so every reveal entry
// point (toggle, hidden chip, Ctrl+H) routes through one gate. UI-only.
export const sensitiveInfoRevealConfirmOpenState = atom<boolean>({
    key: 'sensitiveInfoRevealConfirmOpenState',
    default: false,
});

// Images the user has attested, this session, carry no burned-in patient
// identifiers — the DICOM BurnedInAnnotation attribute is Type 3 for CT/MR and
// so is absent from nearly all planning data, which would otherwise make the
// screenshot tool's confirmation fire on every single capture and train the user
// to dismiss it unread. Holding the attestation per image makes it one prompt
// per image instead. Never persisted, and never lets a DICOM-declared YES
// through. Cleared on patient unload so it cannot carry across patients.
//
// Scoped to the whole Image3D, not a single slice, on purpose. A declared YES
// aggregates fail-safe across every slice (see Image3D.burnedInAnnotationStatus)
// and is never attestable, so the only case a per-image attestation could
// under-cover is non-conformant data — burned-in pixels with the flag absent on
// every slice. Per-slice keying would also be ill-defined for the reformatted
// planes this tool captures (one sagittal/coronal frame samples the entire
// volume). The confirmation copy is therefore scan-scoped, so the user attests
// to the image rather than only the slice on screen.
export const reviewedBurnedInImagesState = atom<ReadonlySet<Image3D>>({
    key: 'reviewedBurnedInImagesState',
    default: new Set(),
    // Image3D is a large mutable engine object; Recoil must not freeze it.
    dangerouslyAllowMutability: true,
});

// Controls visibility of the DICOM Sources modal (Search/Select/Finish)
export const dicomSourcesModalOpenState = atom<boolean>({
    key: 'dicomSourcesModalOpenState',
    default: false,
});

export const autoLoadResultsState = atom<boolean>({
    key: 'autoLoadResultsState',
    default: true,
});

// Three states: null for disabled DVH view, true for shown DVH view, false for hidden DVH view
export const showDVHViewComponentState = atom<boolean | null>({
    key: 'showDVHViewComponentState',
    default: null,
});

export const showDoseStatisticsTableState = atom<boolean | null>({
    key: 'showDoseStatisticsTableState',
    default: null,
});

export enum WorkflowTypes {
    WORKSPACE,
    CONTOUR,
    DOSE,
    IMAGE,
    ADAPT,
    DATA_UPLOAD,
    SEG_VERIFY,
    REGISTER_IMAGES,
    COMPOSITE_CBCT,
}

export const workflowModalState = atom<{
    open: boolean;
    page: WorkflowTypes;
}>({
    key: 'workflowModalState',
    default: {
        open: false,
        page: WorkflowTypes.CONTOUR,
    },
});

export enum ExportTypes {
    STRUCTURES,
    DOSE,
}

export const exportDatasetsModalOpenState = atom<boolean>({
    key: 'exportDatasetsModalOpenState',
    default: false,
});

export const currenPositionAutoScrollState = atom<vec3 | null>({
    key: 'currenPositionAutoScrollState',
    default: null,
});

export const isAutoScrollingState = atom<boolean>({
    key: 'isAutoScrollingState',
    default: false,
});

/**
 * The DICOM Patient ID of the patient whose data the user chose to send to
 * the cloud backend without de-identification, or null.
 */
const identifiableSendPatientIdState = atom<string | null>({
    key: 'identifiableSendPatientIdState',
    default: null,
});

/**
 * Whether patient data sent to the cloud backend is de-identified. Turning it
 * off holds for the selected patient only: with another patient selected, or
 * none, it reads on.
 */
export const pseudonymizeState = selector<boolean>({
    key: 'pseudonymizeState',
    get: ({ get }) => {
        const identifiable = get(identifiableSendPatientIdState);
        return (
            identifiable === null ||
            identifiable !== get(selectedPatientIdState)
        );
    },
    set: ({ get, set }, deidentify) =>
        set(
            identifiableSendPatientIdState,
            deidentify === false ? get(selectedPatientIdState) : null
        ),
});

/**
 * Whether every tile's title bar shows its slice number and sampling in
 * full, rather than on hover of its plane name. Kept across sessions.
 */
export const tileSliceDetailsShownState = atom<boolean>({
    key: 'tileSliceDetailsShownState',
    default: false,
    effects: [persistAtom],
});

export const enableExportMonacoDirFileState = atom<boolean>({
    key: 'enableExportMonacoDirFileState',
    default: false,
    effects: [persistAtom],
});
